import { useQuery } from "react-admin";

const ADMIN_ROLES = ["superadmin", "admin"];
const MANAGE_SIMPLE_PRODUCTS_ROLE = "managesimpleproducts";

export const normalizeRoleName = (role) => {
  const name = typeof role === "string" ? role : role?.name;
  return String(name || "")
    .toLowerCase()
    .replace(/\s+/g, "");
};

export const isManageSimpleProductsRole = (role) =>
  normalizeRoleName(role) === MANAGE_SIMPLE_PRODUCTS_ROLE;

export const SIMPLE_PRODUCTS_ONLY_PERMISSION = "simple_products_only";

export const isAdminRole = (role) =>
  ADMIN_ROLES.includes(normalizeRoleName(role));

export const isRestrictedToSimpleProducts = (roles = []) => {
  const list = Array.isArray(roles) ? roles : [];
  if (!list.some(isManageSimpleProductsRole)) return false;
  return !list.some(isAdminRole);
};

export const parsePermissionsList = (permissions) => {
  if (!permissions) return [];
  if (Array.isArray(permissions)) return permissions;
  return String(permissions).split(",").filter(Boolean);
};

export const hasSimpleProductsOnlyAccess = (permissions) =>
  parsePermissionsList(permissions).includes(SIMPLE_PRODUCTS_ONLY_PERMISSION);

const getRoleId = (role) => (role && typeof role === "object" ? role.id : role);

const resolveRole = (item, allRoles = []) => {
  if (isManageSimpleProductsRole(item)) return item;
  const id = getRoleId(item);
  return allRoles.find((role) => String(role?.id) === String(id));
};

export const selectedRolesIncludeManageSimpleProducts = (
  selectedRoles,
  allRoles = []
) => {
  const selected = Array.isArray(selectedRoles) ? selectedRoles : [];
  return selected.some((item) =>
    isManageSimpleProductsRole(item) ||
    isManageSimpleProductsRole(resolveRole(item, allRoles))
  );
};

export const canManageUserPassword = (currentUser) => {
  const roles = currentUser?.roles || [];
  return roles.some((role) => ADMIN_ROLES.includes(normalizeRoleName(role)));
};

export const useCurrentUser = () => {
  const { data: currentUser, loading } = useQuery({
    type: "getOne",
    resource: "me",
    payload: { id: "" },
  });

  return { currentUser, loading };
};

export const useManageSimpleProductsStoreLock = () => {
  const { currentUser, loading } = useCurrentUser();
  const locked = isRestrictedToSimpleProducts(currentUser?.roles);
  const storeId = currentUser?.store_id ?? currentUser?.store?.id ?? null;

  return { loading, locked, storeId };
};

export const useCanManageUserPassword = () => {
  const { currentUser, loading } = useCurrentUser();

  return {
    loading,
    canManage: !loading && canManageUserPassword(currentUser),
  };
};
